import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Edit, Trash2, GripVertical, Loader2, Check, X, Search, Palette, ArrowUpDown } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_OPTIONS = [
  { value: "bg-sky-500", label: "Sky", hex: "#0ea5e9", textColor: "text-sky-700", bgClass: "bg-sky-100" },
  { value: "bg-blue-500", label: "Blue", hex: "#3b82f6", textColor: "text-blue-700", bgClass: "bg-blue-100" },
  { value: "bg-indigo-500", label: "Indigo", hex: "#6366f1", textColor: "text-indigo-700", bgClass: "bg-indigo-100" },
  { value: "bg-violet-500", label: "Violet", hex: "#8b5cf6", textColor: "text-violet-700", bgClass: "bg-violet-100" },
  { value: "bg-purple-500", label: "Purple", hex: "#a855f7", textColor: "text-purple-700", bgClass: "bg-purple-100" },
  { value: "bg-pink-500", label: "Pink", hex: "#ec4899", textColor: "text-pink-700", bgClass: "bg-pink-100" },
  { value: "bg-rose-500", label: "Rose", hex: "#f43f5e", textColor: "text-rose-700", bgClass: "bg-rose-100" },
  { value: "bg-red-500", label: "Red", hex: "#ef4444", textColor: "text-red-700", bgClass: "bg-red-100" },
  { value: "bg-orange-500", label: "Orange", hex: "#f97316", textColor: "text-orange-700", bgClass: "bg-orange-100" },
  { value: "bg-amber-500", label: "Amber", hex: "#f59e0b", textColor: "text-amber-700", bgClass: "bg-amber-100" },
  { value: "bg-yellow-500", label: "Yellow", hex: "#eab308", textColor: "text-yellow-700", bgClass: "bg-yellow-100" },
  { value: "bg-lime-500", label: "Lime", hex: "#84cc16", textColor: "text-lime-700", bgClass: "bg-lime-100" },
  { value: "bg-green-500", label: "Green", hex: "#22c55e", textColor: "text-green-700", bgClass: "bg-green-100" },
  { value: "bg-emerald-500", label: "Emerald", hex: "#10b981", textColor: "text-emerald-700", bgClass: "bg-emerald-100" },
  { value: "bg-teal-500", label: "Teal", hex: "#14b8a6", textColor: "text-teal-700", bgClass: "bg-teal-100" },
  { value: "bg-slate-500", label: "Slate", hex: "#64748b", textColor: "text-slate-700", bgClass: "bg-slate-100" },
];

function getColorOption(colorValue) {
  return COLOR_OPTIONS.find(c => c.value === colorValue) || COLOR_OPTIONS[COLOR_OPTIONS.length - 1];
}

export default function LeadStageManagement() {
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingStage, setDeletingStage] = useState(null);
  const [deletingLeadsCount, setDeletingLeadsCount] = useState(0);
  const [reassignStage, setReassignStage] = useState("");
  const [saving, setSaving] = useState(false);
  const [userRole, setUserRole] = useState("");
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const [formData, setFormData] = useState({
    label: "",
    color: "bg-slate-500",
  });

  // Fetch user role
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/auth/me`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setUserRole(data.role || "");
        }
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const canManage = ["super_admin", "admin", "manager"].includes(userRole);

  const fetchStages = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/pipeline-stages`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStages(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      toast.error("Failed to fetch stages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStages(); }, [fetchStages]);

  const filteredStages = stages.filter(s =>
    s.label?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openCreateDialog = () => {
    setEditingStage(null);
    setFormData({ label: "", color: "bg-sky-500" });
    setDialogOpen(true);
  };

  const openEditDialog = (stage) => {
    setEditingStage(stage);
    setFormData({ label: stage.label, color: stage.color || "bg-slate-500" });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.label.trim()) {
      toast.error("Stage name is required");
      return;
    }
    setSaving(true);
    try {
      const colorOpt = getColorOption(formData.color);
      if (editingStage) {
        // Update
        const res = await fetch(`${BACKEND_URL}/api/pipeline-stages/${editingStage.stage_id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            label: formData.label.trim(),
            key: formData.label.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
            color: formData.color,
            textColor: colorOpt.textColor,
          }),
        });
        if (res.ok) {
          toast.success("Stage updated successfully");
          setDialogOpen(false);
          fetchStages();
        } else {
          const err = await res.json();
          toast.error(err.detail || "Failed to update stage");
        }
      } else {
        // Create
        const res = await fetch(`${BACKEND_URL}/api/pipeline-stages`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            label: formData.label.trim(),
            color: formData.color,
            textColor: colorOpt.textColor,
          }),
        });
        if (res.ok) {
          toast.success("Stage created successfully");
          setDialogOpen(false);
          fetchStages();
        } else {
          const err = await res.json();
          toast.error(err.detail || "Failed to create stage");
        }
      }
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = async (stage) => {
    setDeletingStage(stage);
    setReassignStage("");
    try {
      const res = await fetch(`${BACKEND_URL}/api/pipeline-stages/${stage.stage_id}/leads-count`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setDeletingLeadsCount(data.leads_count || 0);
      }
    } catch (e) {
      setDeletingLeadsCount(0);
    }
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deletingStage) return;
    if (deletingLeadsCount > 0 && !reassignStage) {
      toast.error("Please select a stage to reassign leads to");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pipeline-stages/${deletingStage.stage_id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reassign_to: reassignStage || undefined }),
      });
      if (res.ok) {
        toast.success("Stage deleted successfully");
        setDeleteDialogOpen(false);
        setDeletingStage(null);
        fetchStages();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete stage");
      }
    } catch (e) {
      toast.error("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // Drag & Drop reorder
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) return;

    const reordered = [...stages];
    const draggedItem = reordered.splice(dragItem.current, 1)[0];
    reordered.splice(dragOverItem.current, 0, draggedItem);

    // Update sort_order locally
    const updated = reordered.map((s, i) => ({ ...s, sort_order: i }));
    setStages(updated);

    dragItem.current = null;
    dragOverItem.current = null;

    // Save to backend
    try {
      await fetch(`${BACKEND_URL}/api/pipeline-stages/reorder`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          stages: updated.map(s => ({ stage_id: s.stage_id, sort_order: s.sort_order })),
        }),
      });
      toast.success("Stage order saved");
    } catch (e) {
      toast.error("Failed to save order");
      fetchStages();
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Lead Stage Management</h1>
          <p className="text-sm text-slate-500">Manage your pipeline stages — add, edit, reorder, or delete stages.</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search stages..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10 rounded-xl"
          />
        </div>
        {canManage && (
          <Button onClick={openCreateDialog} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
            <Plus className="w-4 h-4 mr-2" />
            Add Stage
          </Button>
        )}
      </div>

      {/* Info Banner */}
      {canManage && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-sm text-blue-700">
          <ArrowUpDown className="w-4 h-4 flex-shrink-0" />
          Drag stages to reorder them. Order is reflected in leads pipeline, filters, and forms.
        </div>
      )}

      {/* Stages List */}
      <Card className="rounded-2xl border border-slate-200 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <Palette className="w-5 h-5 text-indigo-600" />
            Pipeline Stages ({stages.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
            </div>
          ) : filteredStages.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {searchQuery ? "No stages match your search." : "No stages configured. Add your first stage to get started."}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredStages.map((stage, index) => {
                const colorOpt = getColorOption(stage.color);
                return (
                  <div
                    key={stage.stage_id}
                    draggable={canManage && !searchQuery}
                    onDragStart={() => handleDragStart(index)}
                    onDragEnter={() => handleDragEnter(index)}
                    onDragEnd={handleDragEnd}
                    onDragOver={e => e.preventDefault()}
                    className={`flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl transition-all ${
                      canManage && !searchQuery ? "cursor-grab active:cursor-grabbing hover:shadow-md hover:border-indigo-300" : ""
                    }`}
                  >
                    {/* Drag Handle */}
                    {canManage && !searchQuery && (
                      <GripVertical className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}

                    {/* Order Number */}
                    <span className="w-7 h-7 flex items-center justify-center rounded-full bg-slate-100 text-slate-600 text-xs font-bold flex-shrink-0">
                      {index + 1}
                    </span>

                    {/* Color Indicator */}
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: colorOpt.hex }} />

                    {/* Stage Name + Badge Preview */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 truncate">{stage.label}</span>
                        <Badge className={`text-[10px] px-2 py-0.5 ${colorOpt.bgClass} ${colorOpt.textColor} border-0`}>
                          Preview
                        </Badge>
                      </div>
                      {stage.key && (
                        <span className="text-xs text-slate-400">key: {stage.key}</span>
                      )}
                    </div>

                    {/* Actions */}
                    {canManage && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => openEditDialog(stage)}
                          className="p-2 rounded-lg text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                          title="Edit stage"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openDeleteDialog(stage)}
                          className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                          title="Delete stage"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white rounded-2xl max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-900">
              {editingStage ? "Edit Stage" : "Add New Stage"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>Stage Name *</Label>
              <Input
                value={formData.label}
                onChange={e => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Contacted, Negotiation, Won"
                className="rounded-xl mt-1"
                autoFocus
              />
            </div>
            <div>
              <Label className="mb-2 block">Stage Color</Label>
              <div className="grid grid-cols-8 gap-2">
                {COLOR_OPTIONS.map(c => (
                  <button
                    key={c.value}
                    onClick={() => setFormData(prev => ({ ...prev, color: c.value }))}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === c.value ? "border-slate-800 scale-110 ring-2 ring-offset-1 ring-slate-400" : "border-transparent hover:border-slate-300"
                    }`}
                    style={{ backgroundColor: c.hex }}
                    title={c.label}
                  />
                ))}
              </div>
            </div>
            {/* Preview */}
            {formData.label && (
              <div className="p-3 bg-slate-50 rounded-xl">
                <Label className="text-xs text-slate-500 mb-1 block">Preview</Label>
                <Badge className={`${getColorOption(formData.color).bgClass} ${getColorOption(formData.color).textColor} border-0 text-sm px-3 py-1`}>
                  {formData.label}
                </Badge>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingStage ? "Save Changes" : "Create Stage"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Stage: {deletingStage?.label}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              {deletingLeadsCount > 0 ? (
                <>
                  <p className="text-slate-700">
                    This stage contains <strong className="text-red-600">{deletingLeadsCount} lead(s)</strong>.
                    You must reassign them to another stage before deleting.
                  </p>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Move leads to:</Label>
                    <Select value={reassignStage} onValueChange={setReassignStage}>
                      <SelectTrigger className="mt-1 rounded-xl">
                        <SelectValue placeholder="Select a stage..." />
                      </SelectTrigger>
                      <SelectContent>
                        {stages
                          .filter(s => s.stage_id !== deletingStage?.stage_id)
                          .map(s => (
                            <SelectItem key={s.stage_id} value={s.label}>{s.label}</SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <p className="text-slate-600">
                  This stage has no leads. Are you sure you want to delete it? This action cannot be undone.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <Button
              onClick={handleDelete}
              disabled={saving || (deletingLeadsCount > 0 && !reassignStage)}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Stage
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
